import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "../assets/images/logo.png";
import {
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  User,
  FileChartColumn,
  HardDrive,
  Server,
  Building2,
  Earth,
  FileCode2,
  Phone,
  BookOpen,
  Mail,
  Cpu,
  Network,
  Database,
  ExternalLink,
} from "lucide-react";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileActiveDropdown, setMobileActiveDropdown] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setActiveDropdown(null);
    setMobileActiveDropdown(null);
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    {
      name: "Domain",
      href: "/domain",
      icon: Earth,
      description: "Alan adı kayıt, transfer ve yönetim hizmetleri",
      dropdown: [
        { name: "Domain Kayıt", href: "/domain-register", description: "Yeni alan adı kaydı yapın" },
        { name: "Domain Transfer", href: "/domain-transfer", description: "Alan adınızı bize taşıyın" },
        { name: "WHOIS Sorgulama", href: "/domain-whois", description: "Alan adı bilgilerini sorgulayın" },
      ],
    },
    {
      name: "Hosting",
      href: "/hosting",
      icon: FileCode2,
      description: "Web siteniz için güvenli ve hızlı hosting çözümleri",
      dropdown: [
        { name: "Web Hosting", href: "/web-hosting", description: "Başlangıç seviyesi hosting" },
        { name: "Plesk Hosting", href: "/plesk-hosting", description: "Plesk kontrol paneli ile hosting" },
        { name: "Reseller Hosting", href: "/reseller-hosting", description: "Kendi hosting işinizi kurun" },
        { name: "WordPress Hosting", href: "/wordpress-hosting", description: "WP optimize hosting" },
      ],
    },
    {
      name: "Sunucu",
      href: "/server",
      icon: HardDrive,
      description: "Yüksek performanslı VDS ve dedicated sunucular",
      dropdown: [
        { name: "Sanal Sunucu (VDS)", href: "/virtual-server", description: "Intel Xeon VDS", icon: Cpu },
        { name: "Ryzen VDS", href: "/ryzen-server", description: "AMD Ryzen 9950X", icon: Cpu },
        { name: "Ekran Kartlı Sunucu", href: "/gpu-server", description: "GPU gücü", icon: Database },
        { name: "Dedicated Sunucu", href: "/dedicated-server", description: "Fiziksel sunucu", icon: Server },
      ],
    },
    {
      name: "Veri Merkezi",
      href: "/datacenter",
      icon: Server,
      description: "Tier III veri merkezi hizmetleri",
      dropdown: [
        { name: "Sunucu Barındırma", href: "/server-location", description: "Co-location hizmeti", icon: Building2 },
        { name: "Subnet Kiralama", href: "/subnet-leasing", description: "IPv4 subnet", icon: Network },
      ],
    },
    {
      name: "Kurumsal",
      href: "/corporate",
      icon: Building2,
      description: "Şirket bilgileri ve yasal belgeler",
      dropdown: [
        { name: "Hakkımızda", href: "/about-us", description: "Creart Cloud hakkında", icon: Building2 },
        { name: "Banka Bilgileri", href: "/bank-information", description: "Havale/EFT bilgileri", icon: Database },
        { name: "Sözleşmeler", href: "/agreements", description: "Yasal belgeler", icon: FileCode2 },
      ],
    },
  ];

  const quickLinks = [
    { name: "Bilgi Bankası", href: "https://blog.creartcloud.com", icon: BookOpen, external: true },
    { name: "Durum Sayfası", href: "https://status.creartcloud.com/status/creartcloud", icon: FileChartColumn, external: true },
    { name: "Müşteri Paneli", href: "https://dash.creartcloud.com", icon: User, external: true },
  ];

  const supportLinks = [
    { icon: Phone, text: "+44 7821 397716", href: "https://wa.me/447821397716" },
    { icon: Mail, text: "info@creartcloud.com", href: "mailto:info@creartcloud.com" },
  ];

  const handleNavigation = (path, external = false) => {
    if (external) {
      window.open(path, "_blank", "noopener,noreferrer");
    } else {
      navigate(path);
    }

    setIsOpen(false);
    setActiveDropdown(null);
    setMobileActiveDropdown(null);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* FIXED HEADER */}
      <nav
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled
            ? "bg-gray-950/95 backdrop-blur-md border-b border-gray-800 shadow-2xl"
            : "bg-gray-950 border-b border-gray-800"
          }`}
      >
        {/* Desktop Top Bar */}
        <div className="hidden md:block border-b border-gray-800 bg-gray-900 text-gray-300">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 text-sm">
            <div className="flex items-center space-x-6">
              {supportLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="flex items-center space-x-2 text-gray-400 transition-colors hover:text-blue-500"
                >
                  <link.icon size={14} />
                  <span>{link.text}</span>
                </a>
              ))}
            </div>

            <div className="flex items-center space-x-6">
              {quickLinks.map(({ name, href, icon: Icon, external }) => (
                <button
                  key={name}
                  onClick={() => handleNavigation(href, external)}
                  className="flex items-center space-x-2 text-gray-400 transition-colors hover:text-blue-500"
                >
                  <Icon size={14} />
                  <span>{name}</span>
                  {external && <ExternalLink size={12} />}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Navbar */}
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex h-16 items-center justify-between md:h-20">
            <Link to="/" onClick={() => setIsOpen(false)} className="shrink-0">
              <img src={Logo} alt="Creart Cloud" className="h-10 w-auto md:h-12" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden items-center space-x-1 md:flex">
              {navLinks.map((link) => (
                <div key={link.name} className="relative dropdown-container">
                  <button
                    onClick={() =>
                      setActiveDropdown(activeDropdown === link.name ? null : link.name)
                    }
                    className={`flex items-center space-x-2 rounded-lg px-4 py-2 font-medium transition-all ${activeDropdown === link.name
                        ? "bg-gray-800 text-white"
                        : "text-gray-300 hover:bg-gray-800 hover:text-white"
                      }`}
                  >
                    <link.icon size={16} />
                    <span>{link.name}</span>
                    {link.dropdown && (
                      <ChevronDown
                        size={14}
                        className={`transition-transform duration-300 ${activeDropdown === link.name ? "rotate-180" : ""
                          }`}
                      />
                    )}
                  </button>

                  {link.dropdown && activeDropdown === link.name && (
                    <div className="absolute left-0 top-full mt-2 w-64 rounded-xl border border-gray-800 bg-gray-900 py-2 shadow-2xl">
                      {link.dropdown.map((item) => (
                        <button
                          key={item.name}
                          onClick={() => handleNavigation(item.href)}
                          className="w-full px-4 py-3 text-left transition-all hover:bg-gray-800"
                        >
                          <div className="flex items-start space-x-3">
                            {item.icon && (
                              <item.icon size={16} className="mt-0.5 text-blue-500" />
                            )}
                            <div>
                              <div className="text-sm font-medium text-white">
                                {item.name}
                              </div>
                              {item.description && (
                                <div className="mt-0.5 text-xs text-gray-400">
                                  {item.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              <div className="ml-4 border-l border-gray-800 pl-4">
                <button
                  onClick={() => handleNavigation("https://dash.creartcloud.com", true)}
                  className="flex items-center space-x-2 rounded-lg bg-blue-600 px-6 py-2.5 font-medium text-white transition-all hover:bg-blue-700"
                >
                  <User size={16} />
                  <span>Giriş Yap</span>
                </button>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="rounded-lg p-2 text-gray-300 transition-colors hover:bg-gray-800 hover:text-white md:hidden"
              aria-label="Mobil menüyü aç/kapat"
            >
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`fixed inset-y-0 right-0 z-50 w-full bg-gray-950 shadow-2xl transition-transform duration-300 md:hidden ${isOpen ? "translate-x-0" : "translate-x-full"
            }`}
        >
          <div className="flex items-center justify-between border-b border-gray-800 bg-gray-900 p-4">
            <img src={Logo} alt="Creart Cloud" className="h-8 w-auto" />
            <button
              onClick={() => setIsOpen(false)}
              className="rounded-lg p-2 text-gray-300 hover:bg-gray-800 hover:text-white"
            >
              <X size={24} />
            </button>
          </div>
          <div className="bg-gray-900 p-4">
            {supportLinks.map((link, index) => (
              <a
                key={index}
                href={link.href}
                className="flex items-center space-x-3 py-2 text-gray-300 hover:text-blue-500"
                onClick={() => setIsOpen(false)}
              >
                <link.icon size={18} />
                <span>{link.text}</span>
              </a>
            ))}
          </div>

          <div className="p-4">
            {navLinks.map((link) => (
              <div key={link.name} className="border-b border-gray-800 last:border-0">
                <button
                  className="flex w-full items-center justify-between rounded-lg p-3 text-left text-gray-100 hover:bg-gray-800"
                  onClick={() => {
                    if (link.dropdown) {
                      setMobileActiveDropdown(
                        mobileActiveDropdown === link.name ? null : link.name
                      );
                    } else {
                      handleNavigation(link.href);
                    }
                  }}
                >
                  <span className="flex items-center space-x-3">
                    <link.icon size={20} className="text-blue-500" />
                    <span>{link.name}</span>
                  </span>

                  {link.dropdown && (
                    <ChevronRight
                      size={20}
                      className={`transition-transform duration-300 ${mobileActiveDropdown === link.name ? "rotate-90" : ""
                        }`}
                    />
                  )}
                </button>

                {link.dropdown && mobileActiveDropdown === link.name && (
                  <div className="ml-11 space-y-1 border-l border-gray-800 pb-2 pl-4">
                    {link.dropdown.map((item) => (
                      <button
                        key={item.name}
                        onClick={() => handleNavigation(item.href)}
                        className="block w-full rounded-lg p-2 text-left text-gray-300 hover:bg-gray-800 hover:text-white"
                      >
                        <div className="flex items-center space-x-2">
                          {item.icon && (
                            <item.icon size={14} className="text-blue-500" />
                          )}
                          <span className="text-sm">{item.name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-gray-900 p-4">
            <h3 className="mb-2 px-3 text-xs text-gray-500">Hızlı Bağlantılar</h3>
            {quickLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavigation(link.href, link.external)}
                className="flex w-full items-center space-x-3 rounded-lg p-3 text-left text-gray-300 hover:bg-gray-800 hover:text-white"
              >
                <link.icon size={18} className="text-blue-500" />
                <span>{link.name}</span>
              </button>
            ))}
          </div>

          <div className="border-t border-gray-800 bg-gray-900 p-4">
            <button
              onClick={() => handleNavigation("https://dash.creartcloud.com", true)}
              className="w-full rounded-lg bg-blue-600 px-6 py-3 font-medium text-white hover:bg-blue-700"
            >
              Müşteri Paneli
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}